import axios, { type AxiosInstance, type AxiosRequestConfig } from 'axios';
import { tenantAuthStore } from '../state/tenantAuth';
import { serializeParams } from '@marine/api-client/api-params';
import { installCsrfRetry } from '@marine/api-client/csrf-retry';
import type { PaginatedResponse, PaginationMeta } from '@marine/api-client/types/api';

/**
 * Tenant API Client
 * 
 * Class-based singleton client for tenant-specific API calls with Sanctum SPA authentication.
 * Mirrors the structure of the main platform ApiClient.
 */
/**
 * #1177 (spec §3.5) — where a dead session sends the dealer: the login page,
 * then back to the page they were on (pending boat changes are restored there).
 */
export function loginRedirectUrl(): string {
  const { pathname, search } = window.location;
  if (pathname.includes('/login')) return `${pathname}${search}`;
  return `/login?redirect=${encodeURIComponent(`${pathname}${search}`)}`;
}

class TenantApiClient {
  private instance: AxiosInstance;

  constructor() {
    this.instance = axios.create({
      baseURL: this.getTenantApiUrl(),
      withCredentials: true, // Include cookies for session auth
      withXSRFToken: true, // Automatically extract XSRF token from cookie and send in header
      paramsSerializer: { serialize: serializeParams },
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest', // Laravel CSRF requirement
      },
    });

    // installCsrfRetry passes { baseURL: '' } on its internal refresh GET, so
    // axios ignores this instance's '/api' baseURL and hits '/sanctum/csrf-cookie'
    // at the site root — where Laravel's web middleware group registers it.
    // The explicit csrfCookieUrl here is kept for clarity but is no longer
    // required to prevent the '/api/sanctum/csrf-cookie' mis-routing.
    installCsrfRetry(this.instance, {
      csrfCookieUrl: '/sanctum/csrf-cookie',
      onSessionDead: () => {
        localStorage.removeItem('tenant-auth-storage');
        tenantAuthStore.getState().logout();
        if (!window.location.pathname.includes('/login')) {
          window.location.href = loginRedirectUrl();
        }
      },
    });
    this.setupInterceptors();
  }

  /**
   * Get API base URL for tenant
   * 
   * In development: Uses relative URL to leverage Vite proxy (avoids cross-origin issues)
   * In production: Relative URL resolves to same domain as frontend
   */
  private getTenantApiUrl(): string {
    // Use relative URL in browser - Vite proxy handles it in dev, works in production
    if (typeof window !== 'undefined') {
      return '/api';
    }
    
    // Fallback for SSR or other environments (not used in this SPA)
    const baseURL = import.meta.env.VITE_API_URL;
    return baseURL ? `${baseURL}/api` : '/api';
  }

  private setupInterceptors() {
    // Error interceptor: global error handling (401/404)
    this.instance.interceptors.response.use(
      (response) => response,
      (error) => {
        const status = error.response?.status;
        const errorCode = error.response?.data?.error_code;

        // Redirect to tenant login on 401
        if (status === 401) {
          localStorage.removeItem('tenant-auth-storage');
          tenantAuthStore.getState().logout();

          // Only redirect if not already on login page
          if (!window.location.pathname.includes('/login')) {
            window.location.href = loginRedirectUrl();
          }
        }

        // Tenant not found (usually wrong subdomain)
        if (status === 404 && errorCode === 'TENANT_NOT_FOUND') {
          console.error('Tenant not found - check subdomain');
        }

        return Promise.reject(error);
      }
    );
  }

  /**
   * Get CSRF cookie before making state-changing requests
   * Must be called before login for Sanctum SPA auth
   */
  async getCsrfCookie(): Promise<void> {
    // Use relative URL to go through Vite proxy
    await axios.get('/sanctum/csrf-cookie', {
      withCredentials: true,
    });
  }

  /**
   * Generic HTTP methods exposing the axios instance
   */
  get<T = any>(url: string, config?: AxiosRequestConfig) {
    return this.instance.get<T>(url, config);
  }

  post<T = any>(url: string, data?: any, config?: AxiosRequestConfig) {
    return this.instance.post<T>(url, data, config);
  }

  put<T = any>(url: string, data?: any, config?: AxiosRequestConfig) {
    return this.instance.put<T>(url, data, config);
  }

  patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig) {
    return this.instance.patch<T>(url, data, config);
  }

  delete<T = any>(url: string, config?: AxiosRequestConfig) {
    return this.instance.delete<T>(url, config);
  }

  // ── Typed convenience methods (unwrap Laravel {data:…} envelope) ──

  /** Single resource: backend {data: T} -> returns T */
  async getOne<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.instance.get<{ data: T }>(url, config);
    return response.data.data;
  }

  /** Non-paginated collection: backend {data: T[]} -> returns T[] */
  async getList<T>(url: string, config?: AxiosRequestConfig): Promise<T[]> {
    const response = await this.instance.get<{ data: T[] }>(url, config);
    return response.data.data;
  }

  /** Paginated collection: backend {data: T[], meta: {...}, ...extras} -> returns PaginatedResponse<T> */
  async getPaginated<T>(url: string, config?: AxiosRequestConfig): Promise<PaginatedResponse<T>> {
    const response = await this.instance.get<{ data: T[]; meta: PaginationMeta; [key: string]: unknown }>(url, config);
    const { data, meta, ...extra } = response.data;
    return { items: data, meta: { ...meta, ...extra } as PaginationMeta };
  }

  /** Create/action returning data: backend {data: T} -> returns T */
  async postOne<T = void>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.instance.post<{ data: T }>(url, data, config);
    return response.data.data;
  }

  /** For POST that returns {message: "..."} with no data */
  async postAction(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<string> {
    const response = await this.instance.post<{ message: string }>(url, data, config);
    return response.data.message;
  }

  /** Update: backend {data: T} -> returns T */
  async putOne<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.instance.put<{ data: T }>(url, data, config);
    return response.data.data;
  }

  /** Patch: backend {data: T} -> returns T */
  async patchOne<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.instance.patch<{ data: T }>(url, data, config);
    return response.data.data;
  }

  /** Delete: backend {message: "..."} -> returns message */
  async deleteOne(url: string, config?: AxiosRequestConfig): Promise<string> {
    const response = await this.instance.delete<{ message: string }>(url, config);
    return response.data.message;
  }

  /**
   * Get raw axios instance if needed for advanced usage
   */
  getInstance(): AxiosInstance {
    return this.instance;
  }
}

// Export singleton instance
export const tenantApi = new TenantApiClient();
